import React, { useState, useEffect } from "react";

import ClubbieModal from "../../../Modal/ClubbieModal";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";

const NumberInput = ({ value, onChange, min = 0, max = 999 }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => {
      const v = parseInt(e.target.value);
      if (!isNaN(v) && v >= min && v <= max) onChange(v);
    }}
    min={min}
    max={max}
    className="w-full h-10 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
  />
);

const InlineNumberInput = ({ value, onChange, min = 0, max = 999, disabled = false }) => (
  <input
    type="number"
    value={disabled ? "" : value}
    disabled={disabled}
    onChange={(e) => {
      const v = parseInt(e.target.value);
      if (!isNaN(v) && v >= min && v <= max) onChange(v);
    }}
    min={min}
    max={max}
    className={`w-16 h-9 px-3 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors text-center ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  />
);

const GroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  mode = "create",
  initialData = { name: "", numGroups: 1, teamsPerGroup: 4 },
  phaseId = "",
}) => {
  const [groupConfig, setGroupConfig] = useState({
    numGroups: 1,
    teamsPerGroup: 4,
    timesToFaceEachOther: 1,
    fixtureMode: "ROUND_ROBIN",
    matchesPerTeam: 1,
  });

  useEffect(() => {
    if (isOpen) {
      setGroupConfig({
        numGroups: initialData.numGroups || 1,
        teamsPerGroup: initialData.teamsPerGroup || 4,
        timesToFaceEachOther: initialData.timesToFaceEachOther || 1,
        fixtureMode: initialData.fixtureMode || "ROUND_ROBIN",
        matchesPerTeam: initialData.matchesPerTeam || 1,
      });
    }
  }, [isOpen, initialData]);

  const isCreate = mode === "create";

  const handleSubmit = () => {
    onSubmit({
      name: initialData.name || "Group",
      numGroups: parseInt(groupConfig.numGroups) || 1,
      teamsPerGroup: parseInt(groupConfig.teamsPerGroup),
      timesToFaceEachOther: groupConfig.timesToFaceEachOther,
      fixtureMode: groupConfig.fixtureMode,
      matchesPerTeam: groupConfig.matchesPerTeam,
    });
  };

  const renderFooter = () => (
    <div className="flex justify-end gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        type="button"
        className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        onClick={handleSubmit}
        disabled={isLoading || !groupConfig.teamsPerGroup}
      >
        {isLoading ? (
          <>
            <LoadingSpinner size="small" />
            <span>{isCreate ? "Creating..." : "Saving..."}</span>
          </>
        ) : isCreate ? (
          "Create"
        ) : (
          "Save"
        )}
      </button>
    </div>
  );

  return (
    <ClubbieModal
      isOpen={isOpen}
      width="680px"
      maxHeight="90vh"
      toggle={onClose}
      title={isCreate ? `Add group${phaseId ? ` to ${phaseId}` : ""}` : "Edit group"}
      footer={renderFooter()}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            {isCreate && (
              <div className="flex flex-col gap-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Number of groups</label>
                <NumberInput
                  value={groupConfig.numGroups}
                  onChange={(v) => setGroupConfig((p) => ({ ...p, numGroups: v }))}
                  min={1}
                  max={32}
                />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label className="block text-[14px] font-normal text-clubbie-white ">Teams per group</label>
              <NumberInput
                value={groupConfig.teamsPerGroup}
                onChange={(v) => setGroupConfig((p) => ({ ...p, teamsPerGroup: v }))}
                min={2}
                max={64}
              />
            </div>
          </div>

          {/* Fixture Mode Selection */}
          <div className="flex flex-col gap-3">
            <label className="block text-[14px] font-normal text-clubbie-white ">Fixture scheduling mode</label>
            <div className="flex flex-col gap-[6px]">
              <div className="flex items-center gap-[12px]">
                <label className="flex items-center gap-[12px] cursor-pointer">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      groupConfig.fixtureMode === "ROUND_ROBIN" ? "border-[#00E7FF]" : "border-[#333E48]"
                    }`}
                  >
                    {groupConfig.fixtureMode === "ROUND_ROBIN" && <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />}
                  </div>
                  <input
                    type="radio"
                    className="hidden"
                    checked={groupConfig.fixtureMode === "ROUND_ROBIN"}
                    onChange={() => setGroupConfig((p) => ({ ...p, fixtureMode: "ROUND_ROBIN" }))}
                  />
                  <span className="text-sm text-[#FEFCEB]">Times to face each other</span>
                </label>
                <InlineNumberInput
                  value={groupConfig.timesToFaceEachOther}
                  onChange={(v) => setGroupConfig((p) => ({ ...p, timesToFaceEachOther: v }))}
                  min={1}
                  max={4}
                  disabled={groupConfig.fixtureMode !== "ROUND_ROBIN"}
                />
              </div>
              <div className="flex items-center gap-[12px]">
                <label className="flex items-center gap-[12px] cursor-pointer">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      groupConfig.fixtureMode === "FIXED_MATCHES" ? "border-[#00E7FF]" : "border-[#333E48]"
                    }`}
                  >
                    {groupConfig.fixtureMode === "FIXED_MATCHES" && <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />}
                  </div>
                  <input
                    type="radio"
                    className="hidden"
                    checked={groupConfig.fixtureMode === "FIXED_MATCHES"}
                    onChange={() => setGroupConfig((p) => ({ ...p, fixtureMode: "FIXED_MATCHES" }))}
                  />
                  <span className="text-sm text-[#FEFCEB]">Number of matches per team</span>
                </label>
                <InlineNumberInput
                  value={groupConfig.matchesPerTeam}
                  onChange={(v) => setGroupConfig((p) => ({ ...p, matchesPerTeam: v }))}
                  min={1}
                  max={99}
                  disabled={groupConfig.fixtureMode !== "FIXED_MATCHES"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClubbieModal>
  );
};

export default GroupModal;
